// src/hooks/usePlatformNotifications.ts
//
// React Query hook for platform-level notifications.
// Provides: full list (for bell dropdown), unread count (for bell badge),
// mark-single-read, and mark-all-read mutations with optimistic updates.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  fetchPlatformNotifications,
  fetchUnreadCount,
  markPlatformNotificationAsRead,
  markAllPlatformNotificationsAsRead,
  type PlatformNotification,
} from '../services/platformNotificationsApi'

export const NOTIFICATIONS_KEY = ['platformNotifications'] as const
export const UNREAD_COUNT_KEY = ['platformNotificationsUnreadCount'] as const

export const usePlatformNotifications = () => {
  const queryClient = useQueryClient()

  // ── Full list ──────────────────────────────────────────────────────────────
  const notificationsQuery = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () => fetchPlatformNotifications(),
    refetchOnWindowFocus: true,
    retry: 1,
    staleTime: 30_000, // 30 s
  })

  // ── Unread count (cheap badge poll) ───────────────────────────────────────
  const unreadCountQuery = useQuery({
    queryKey: UNREAD_COUNT_KEY,
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000, // poll every 60 s
    retry: 1,
    staleTime: 30_000,
  })

  // ── Mark single as read ───────────────────────────────────────────────────
  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => markPlatformNotificationAsRead(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous = queryClient.getQueryData<PlatformNotification[]>(NOTIFICATIONS_KEY)

      if (previous) {
        queryClient.setQueryData<PlatformNotification[]>(
          NOTIFICATIONS_KEY,
          previous.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
        )
      }

      // Decrement optimistically
      const prevCount = queryClient.getQueryData<{ unread_count: number }>(UNREAD_COUNT_KEY)
      if (prevCount && prevCount.unread_count > 0) {
        queryClient.setQueryData(UNREAD_COUNT_KEY, {
          unread_count: prevCount.unread_count - 1,
        })
      }

      return { previous, prevCount }
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_KEY, context.previous)
      }
      if (context?.prevCount) {
        queryClient.setQueryData(UNREAD_COUNT_KEY, context.prevCount)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })

  // ── Mark all as read ─────────────────────────────────────────────────────
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllPlatformNotificationsAsRead,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATIONS_KEY })
      const previous = queryClient.getQueryData<PlatformNotification[]>(NOTIFICATIONS_KEY)

      if (previous) {
        queryClient.setQueryData<PlatformNotification[]>(
          NOTIFICATIONS_KEY,
          previous.map((n) => ({ ...n, is_read: true })),
        )
      }

      queryClient.setQueryData(UNREAD_COUNT_KEY, { unread_count: 0 })

      return { previous }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATIONS_KEY, context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY })
      queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY })
    },
  })

  const notifications = notificationsQuery.data ?? []
  const unreadCount =
    unreadCountQuery.data?.unread_count ??
    notifications.filter((n) => !n.is_read).length

  return {
    notifications,
    unreadCount,
    isLoading: notificationsQuery.isLoading,
    isError: notificationsQuery.isError,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    // expose raw queries for components that need full EXPIRY_ALERT data
    notificationsQuery,
    unreadCountQuery,
  }
}
